import React, { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";
import Overview from "./views/Overview";
import Properties from "./views/Properties";
import Pipeline from "./views/Pipeline";
import AgentPipeline from "./views/AgentPipeline";
import Agents from "./views/Agents";
import BlackBook from "./views/BlackBook";
import AIInsights from "./views/AIInsights";
import SettingsView from "./views/SettingsView";
import { useAuth } from "../context/AuthContext";
import { Eye, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";

export default function DashboardShell() {
  const { acting, isStaff, stopActing } = useAuth();
  const { t } = useLang();
  const [active, setActive] = useState(() => sessionStorage.getItem("mb_view") || "overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const h = (e) => { sessionStorage.setItem("mb_view", e.detail); setActive(e.detail); };
    window.addEventListener("mb:navigate", h);
    return () => window.removeEventListener("mb:navigate", h);
  }, []);

  const exitImpersonation = () => {
    stopActing();
    sessionStorage.removeItem("mb_view");
    sessionStorage.removeItem("mb_detail");
    window.location.assign("/admin");
  };

  const navigate = (view) => {
    if (view !== "properties") sessionStorage.removeItem("mb_detail"); // leaving Properties closes any open detail
    sessionStorage.setItem("mb_view", view);
    setActive(view);
  };

  const handleNewDeal = () => {
    navigate("properties");
    toast(t("Add a property"), { description: t("Use “Add” or “Mubawab” to start a new deal.") });
  };

  const render = () => {
    switch (active) {
      case "properties": return <Properties />;
      case "pipeline": return <Pipeline />;
      case "agent-pipeline": return <AgentPipeline />;
      case "agents": return <Agents />;
      case "blackbook": return <BlackBook />;
      case "insights": return <AIInsights />;
      case "settings": return <SettingsView />;
      default: return <Overview onNavigate={navigate} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar active={active} onNavigate={navigate} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <div className="flex min-w-0 flex-1 flex-col">
        {isStaff && acting && (
          acting.self ? (
            <div className="flex items-center justify-between gap-3 bg-brand-green px-4 py-2 text-white sm:px-6" data-testid="own-crm-banner">
              <div className="flex items-center gap-2 text-sm">
                <Eye className="h-4 w-4" />
                <span>{t("Your workspace — this is your own CRM data")}</span>
              </div>
              <button onClick={exitImpersonation} className="inline-flex items-center gap-1 rounded-lg bg-white/15 px-3 py-1.5 text-sm font-medium hover:bg-white/25 transition-colors" data-testid="exit-impersonation-btn">
                <ArrowLeft className="h-4 w-4" /> {t("Back to console")}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 bg-brand-red px-4 py-2 text-white sm:px-6" data-testid="impersonation-banner">
              <div className="flex items-center gap-2 text-sm">
                <Eye className="h-4 w-4" />
                <span>{t("Viewing as")} <span className="font-semibold">{acting.name}</span> — {t("changes save to their account")}</span>
              </div>
              <button onClick={exitImpersonation} className="inline-flex items-center gap-1 rounded-lg bg-white/15 px-3 py-1.5 text-sm font-medium hover:bg-white/25 transition-colors" data-testid="exit-impersonation-btn">
                <ArrowLeft className="h-4 w-4" /> {t("Back to console")}
              </button>
            </div>
          )
        )}
        <Topbar active={active} onMenu={() => setMobileOpen(true)} onNewDeal={handleNewDeal} />
        <main className="flex-1 overflow-x-hidden px-4 py-6 sm:px-6">
          <div key={active} className="animate-fade-up">{render()}</div>
        </main>
      </div>
    </div>
  );
}
